import React from 'react';
import { Container, Box, Typography } from '@mui/material';
import { AccessTime, CalendarToday, Person } from '@mui/icons-material';
import BreadcrumbNav from '../SEO/BreadcrumbNav';

/**
 * Unified page header for all FinMaple pages (except HomePage).
 *
 * Variants:
 * - 'standard'    - branded banner with title + subtitle (default)
 * - 'article'     - branded banner with title + subtitle + metadata row
 * - 'minimal'     - no colored banner, renders on page background
 * - 'functional'  - light gradient bg (comparator, hub, landing pages)
 * - 'tool'        - dark gradient bg (calculators, budget planner)
 */
const PageHeader = ({
  title,
  subtitle,
  breadcrumbs,
  variant = 'standard',
  metadata,
  maxWidth = 'md',
  children,
  overline,
}) => {
  // ── Functional variant ──────────────────────────────────────────
  if (variant === 'functional') {
    return (
      <Box
        sx={{
          position: 'relative',
          background: 'linear-gradient(180deg, #F7F9FC 0%, #F7F9FC 100%)',
          borderBottom: '1px solid #E6E9EE',
          py: { xs: 3.5, md: 4.5 },
          overflow: 'hidden',
        }}
      >
        {/* Radial glow overlay */}
        <Box
          sx={{
            position: 'absolute',
            inset: 0,
            background: 'radial-gradient(circle at 70% 30%, rgba(30,58,95,0.06), transparent 60%)',
            pointerEvents: 'none',
          }}
        />
        <Container maxWidth={maxWidth} sx={{ position: 'relative', zIndex: 1 }}>
          {breadcrumbs && (
            <Box sx={{ mb: 2 }}>
              <BreadcrumbNav items={breadcrumbs} />
            </Box>
          )}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              color: '#1A1A1A',
              mb: subtitle ? 1.5 : 0,
              lineHeight: 1.2,
              fontSize: { xs: '1.6rem', md: '2.4rem' },
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="h6"
              sx={{
                color: '#1A1A1A',
                fontWeight: 400,
                maxWidth: 640,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {children}
        </Container>
      </Box>
    );
  }

  // ── Tool variant ────────────────────────────────────────────────
  if (variant === 'tool') {
    return (
      <Box
        sx={{
          background: 'linear-gradient(135deg, #1A1A1A 0%, #1E3A5F 100%)',
          color: '#fff',
          py: { xs: 6, md: 8 },
        }}
      >
        <Container maxWidth={maxWidth}>
          {breadcrumbs && (
            <Box sx={{ mb: 2, '& a, & span': { opacity: 0.6, color: '#fff' } }}>
              <BreadcrumbNav items={breadcrumbs} />
            </Box>
          )}
          <Typography
            variant="h3"
            component="h1"
            sx={{
              fontWeight: 600,
              color: '#FFFFFF',
              mb: subtitle ? 1.5 : 0,
              lineHeight: 1.2,
              fontSize: { xs: '1.6rem', md: '2.4rem' },
            }}
          >
            {title}
          </Typography>
          {subtitle && (
            <Typography
              variant="h6"
              sx={{
                color: 'rgba(255,255,255,0.7)',
                fontWeight: 400,
                maxWidth: 640,
              }}
            >
              {subtitle}
            </Typography>
          )}
          {children}
        </Container>
      </Box>
    );
  }

  // ── Minimal variant ─────────────────────────────────────────────
  if (variant === 'minimal') {
    return (
      <Container maxWidth={maxWidth} sx={{ pt: { xs: 1, md: 1.5 }, textAlign: 'center' }}>
        {/* Breadcrumbs inline above title */}
        {breadcrumbs && <BreadcrumbNav items={breadcrumbs} />}

        <Typography
          variant="h3"
          component="h1"
          sx={{
            fontWeight: 600,
            color: '#1A1A1A',
            mb: 0.15,
            lineHeight: 1.1,
            fontSize: { xs: '1.3rem', md: '1.55rem' },
            maxWidth: 600,
            mx: 'auto',
          }}
        >
          {title}
        </Typography>

        {subtitle && (
          <Typography
            variant="h6"
            sx={{ color: '#5F6B7A', fontWeight: 400, mb: 0.25, fontSize: { xs: '0.9rem', md: '1rem' }, lineHeight: 1.4, maxWidth: 540, mx: 'auto' }}
          >
            {subtitle}
          </Typography>
        )}

        {/* Metadata row - minimal, disappears visually */}
        {metadata && (
          <Box
            sx={{
              display: 'flex',
              flexWrap: 'wrap',
              alignItems: 'center',
              gap: { xs: 1.5, md: 2.5 },
              mb: 3,
              pb: 2,
              borderBottom: '1px solid #F7F9FC',
            }}
          >
            {metadata.author && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <Person sx={{ fontSize: 13, color: '#78909C' }} />
                <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 500, fontSize: '0.75rem' }}>
                  {metadata.author}
                </Typography>
              </Box>
            )}
            {metadata.date && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <CalendarToday sx={{ fontSize: 12, color: '#78909C' }} />
                <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 500, fontSize: '0.75rem' }}>
                  {metadata.date}
                </Typography>
              </Box>
            )}
            {metadata.readTime && (
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                <AccessTime sx={{ fontSize: 12, color: '#78909C' }} />
                <Typography variant="caption" sx={{ color: '#78909C', fontWeight: 500, fontSize: '0.75rem' }}>
                  {metadata.readTime}
                </Typography>
              </Box>
            )}
            {children}
          </Box>
        )}

        {/* Children without metadata */}
        {!metadata && children}
      </Container>
    );
  }

  // Standard + Article variants - branded banner
  return (
    <>
      <Box
        sx={{
          backgroundColor: '#1E3A5F',
          color: '#fff',
          py: { xs: 4, md: 5 },
          textAlign: 'center',
        }}
      >
        <Container maxWidth={maxWidth}>
          {overline && (
            <Typography
              variant="h6"
              sx={{ opacity: 0.8, mb: 1, fontWeight: 400 }}
            >
              {overline}
            </Typography>
          )}

          <Typography
            variant="h3"
            component="h1"
            className={variant === 'article' ? 'article-headline' : undefined}
            sx={{
              fontWeight: 600,
              mb: subtitle || (variant === 'article' && metadata) ? 2 : 0,
              fontSize: { xs: '1.6rem', md: '2.4rem' },
            }}
          >
            {title}
          </Typography>

          {subtitle && (
            <Typography
              variant="h6"
              sx={{
                opacity: 0.9,
                maxWidth: 600,
                mx: 'auto',
                fontWeight: 400,
                mb: variant === 'article' && metadata ? 2 : 0,
              }}
            >
              {subtitle}
            </Typography>
          )}

          {/* Article metadata row */}
          {variant === 'article' && metadata && (
            <Box
              sx={{
                display: 'flex',
                flexWrap: 'wrap',
                justifyContent: 'center',
                gap: 3,
                opacity: 0.7,
              }}
            >
              {metadata.date && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <CalendarToday sx={{ fontSize: 16 }} />
                  <Typography variant="body2">{metadata.date}</Typography>
                </Box>
              )}
              {metadata.readTime && (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                  <AccessTime sx={{ fontSize: 16 }} />
                  <Typography variant="body2">{metadata.readTime}</Typography>
                </Box>
              )}
              {metadata.author && (
                <Typography variant="body2">{metadata.author}</Typography>
              )}
            </Box>
          )}

          {/* Extra content inside hero (search box, CTA button, etc.) */}
          {children}
        </Container>
      </Box>

      {/* Breadcrumbs below hero on page background */}
      {breadcrumbs && (
        <Container maxWidth={maxWidth} sx={{ pt: { xs: 2, md: 3 } }}>
          <BreadcrumbNav items={breadcrumbs} />
        </Container>
      )}
    </>
  );
};

export default PageHeader;
